import {useEffect, type FC} from "react"
import {Box, Skeleton, Stack, Typography} from "@mui/material"
import {thoughtPiecesEndpoints} from "../../../services/api/thought-pieces"
import {useQuery} from "@tanstack/react-query"
import {ThoughtPiecesModel} from "../../../types/ThoughtPiecesModel"
import {ErrorMessage} from "../../molecules/ErrorMessage"
import {useConfigProvider} from "../../../config"
import {useClientSessionStore} from "../../../stores/clientSessionStore"
import ReponsiveImage from "../../atoms/responsiveImage/ResponsiveImage"
import {DiscoverMainItem} from "../../molecules/DiscoverMainItem"
import TextSeparator from "../../atoms/separator/TextSeparator"
import {useTranslation} from "react-i18next"
import moment from "moment"
import {useSessionStorage} from "usehooks-ts"

const DiscoverThoughtPieceMainSection: FC = () => {
  const {getThoughtPiecesMainAnalysis} = thoughtPiecesEndpoints()
  const {cmsImageUrl} = useConfigProvider()
  const sessionClient = useClientSessionStore((state) => state.crmAccount)
  const {t} = useTranslation()
  const {
    data: thoughtPieces,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    isLoadingError,
    isRefetchError,
    refetch,
  } = useQuery<ThoughtPiecesModel>({
    queryKey: ["getThoughtPiecesMainAnalysis"],
    queryFn: async () => await getThoughtPiecesMainAnalysis(),
    enabled: !!sessionClient,
  })

  const [language] = useSessionStorage("language", "en")
  useEffect(() => {
    if (language) {
      moment.locale(language)
    }
  }, [language])

  return (
    <Box
      position={"relative"}
      borderRadius={"8px"}
      height={"543px"}
      display={"flex"}
      padding={"8px"}
      flexDirection={"column"}
      sx={{
        border: "solid 1px #eeeeee",
        boxShadow: "0px 4px 4px 0px #EEEEEE",
      }}
    >
      <Typography
        align="left"
        color={"#BA0E68"}
        fontSize={14}
        fontStyle={"Open Sans"}
        fontWeight={600}
        padding={1}
        paddingBottom={0.5}
      >
        {t("Thought pieces")}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        gap="0.5rem"
        sx={{
          cursor: "pointer",
          position: "absolute",
          top: "-8px",
          right: "-7px",
        }}
      />
      {!sessionClient ||
      isLoading ||
      isFetching ||
      isRefetching ||
      (!thoughtPieces && !isError && !isLoadingError && !isRefetchError) ? (
        [1]?.map(() => (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                borderRadius: "8px",
              }}
            >
              <Box
                style={{
                  paddingTop: "0px",
                  paddingLeft: "0px",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  height={200}
                  sx={{
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                    marginRight: "8px",
                    marginLeft: "8px",
                    marginTop: "4px",
                  }}
                />
              </Box>
            </Box>
            <Box
              style={{
                paddingTop: "0px",
                paddingLeft: "0px",
                width: "100%",
                marginLeft: "8px",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <Box
                style={{
                  height: "100%",
                  paddingTop: "24px",
                  width: "50%",
                }}
              >
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                    marginTop: "-10px",
                  }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                  }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                  }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                  }}
                />
              </Box>
              <Box
                style={{
                  height: "100%",
                  paddingTop: "24px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  width: "50%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignSelf: "start",
                    marginBottom: "16px",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignSelf: "start",
                      width: "100%",
                    }}
                  >
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "4px"}}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignSelf: "start",
                    width: "100%",
                  }}
                >
                  <Stack direction="row" width="100%">
                    <Box width="100%">
                      <Skeleton animation="wave" variant="text" height={26} />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                      }}
                    >
                      <TextSeparator
                        separator="|"
                        distanceX="7px"
                        fontSize="12px"
                        paddingTop="2.5px"
                      />
                      <Skeleton
                        animation="wave"
                        variant="text"
                        height={26}
                        width="100%"
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                      }}
                    >
                      <TextSeparator
                        separator="|"
                        distanceX="7px"
                        fontSize="12px"
                        paddingTop="2.5px"
                      />
                      <Skeleton
                        animation="wave"
                        variant="text"
                        height={26}
                        width="100%"
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box width="33%">
                  <Skeleton animation="wave" variant="text" height={26} />
                </Box>
              </Box>
            </Box>
          </Box>
        ))
      ) : !thoughtPieces ||
        isError ||
        isLoadingError ||
        isRefetchError ||
        thoughtPieces == undefined ? (
        [1]?.map(() => (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                borderRadius: "8px",
                filter: "blur(5px)",
              }}
            >
              <Box
                style={{
                  paddingTop: "0px",
                  paddingLeft: "0px",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  height={200}
                  sx={{
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                    padding: "8px",
                    paddingTop: "4px",
                  }}
                />
              </Box>
            </Box>
            <Box
              style={{
                paddingTop: "0px",
                paddingLeft: "0px",
                width: "100%",
                marginLeft: "8px",
                display: "flex",
                flexDirection: "row",
                filter: "blur(5px)",
              }}
            >
              <Box
                style={{
                  height: "100%",
                  paddingTop: "24px",
                  width: "50%",
                }}
              >
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                    marginTop: "-10px",
                  }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                  }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                  }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={70}
                  style={{
                    marginBottom: "-12px",
                  }}
                />
              </Box>
              <Box
                style={{
                  height: "100%",
                  paddingTop: "24px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  width: "50%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignSelf: "start",
                    marginBottom: "16px",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignSelf: "start",
                      width: "100%",
                    }}
                  >
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "-4px"}}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={27}
                      style={{marginBottom: "4px"}}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignSelf: "start",
                    width: "100%",
                  }}
                >
                  <Stack direction="row" width="100%">
                    <Box width="100%">
                      <Skeleton animation="wave" variant="text" height={26} />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                      }}
                    >
                      <TextSeparator
                        separator="|"
                        distanceX="7px"
                        fontSize="12px"
                        paddingTop="2.5px"
                      />
                      <Skeleton
                        animation="wave"
                        variant="text"
                        height={26}
                        width="100%"
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                      }}
                    >
                      <TextSeparator
                        separator="|"
                        distanceX="7px"
                        fontSize="12px"
                        paddingTop="2.5px"
                      />
                      <Skeleton
                        animation="wave"
                        variant="text"
                        height={26}
                        width="100%"
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box width="33%">
                  <Skeleton animation="wave" variant="text" height={26} />
                </Box>
              </Box>
            </Box>
            <ErrorMessage
              message={"This content is not available right now"}
              messageStyle={{fontSize: "14px", color: "#595958"}}
              refetch={refetch}
            ></ErrorMessage>
          </Box>
        ))
      ) : (
        <Box
          sx={{
            padding: "8px",
            paddingTop: "4px",
            textDecoration: "none",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <ReponsiveImage
              imageLargePath={cmsImageUrl + thoughtPieces.imageLarge}
              imageMediumPath={cmsImageUrl + thoughtPieces.imageMedium}
              imageSmallPath={cmsImageUrl + thoughtPieces.imageSmall}
              style={{
                borderTopLeftRadius: "4px",
                borderBottomLeftRadius: "4px",
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
                width: "100%",
                height: "200px",
              }}
            ></ReponsiveImage>
          </Box>
          <Box
            style={{
              paddingTop: "20px",
              paddingLeft: "0px",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <DiscoverMainItem
              title={thoughtPieces.title}
              paragraph={thoughtPieces.textBlockValues
                .join(" ")
                .substring(0, 999)}
              authors={thoughtPieces.authors}
              date={moment(thoughtPieces.date)
                .format("D MMM, YYYY")
                .replace(".", "")}
              articleTag={
                thoughtPieces.servicesDisplayTags &&
                thoughtPieces.servicesDisplayTags[0]
                  ? thoughtPieces.servicesDisplayTags[0]
                  : thoughtPieces.mainTagTitle
                  ? thoughtPieces.mainTagTitle
                  : "Our Thinking"
              }
              itemId={thoughtPieces.group}
              itemUrl={thoughtPieces.itemUrl}
            ></DiscoverMainItem>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default DiscoverThoughtPieceMainSection
